import { forwardRef, isValidElement, cloneElement } from 'react';
import {
  useMergeRefs,
  FloatingPortal,
  FloatingArrow,
} from '@floating-ui/react';

import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/tooltip.module.scss';
import { useTooltipContext, TooltipContext } from './context';
import { useTooltip } from './hooks/useTooltip';

import type { HTMLProps } from 'react';
import type { ITooltipOptions } from './types';


export const Tooltip = ({
  children,
  ...options
}: { children: React.ReactNode; } & ITooltipOptions) => {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const tooltip = useTooltip(options);
  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
    </TooltipContext.Provider>
  );
};

export const TooltipTrigger = forwardRef<HTMLElement, HTMLProps<HTMLElement> & { asChild?: boolean; }>(({ children, className, asChild = false, ...props }, propRef) => {
  const context = useTooltipContext();
  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && isValidElement(children)) {
    return cloneElement(
      children,
      context.getReferenceProps({
        ref,
        className: generateClassName([styles.tooltipTrigger, className, children.props.className]),
        ...props,
        ...children.props,
        'data-state': context.open ? 'open' : 'closed',
      }),
    );
  }

  return (
    <span
      ref={ref}
      className={generateClassName([styles.tooltipTrigger, className])}
      // The user can style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
    >
      {children}
    </span>
  );
});

export const TooltipContent = forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>(({ style, className, children, ...props }, propRef) =>{
  const { 
    refs,
    open,
    floatingStyles,
    arrowRef,
    getFloatingProps,
    context,
  } = useTooltipContext();

  const tooltipRef = useMergeRefs([refs.setFloating, propRef]);

  if (!open) return null;

  const contents = (
  <>
    {children}
    <FloatingArrow className={styles.arrow} width={12} height={7} ref={arrowRef} context={context}/>
  </>);
  
  return (
    <FloatingPortal>
      <div
        className={generateClassName([className, styles.tooltip])}
        ref={tooltipRef}
        style={{
          ...floatingStyles,
          ...style,
        }}
        {...getFloatingProps(props)}
      >
        {contents}
      </div>
    </FloatingPortal>
  );
});
